/**
 * Phase 4g.I (Feb 2026) — B2C Buyer-Portal Page (login-frei, Magic-Link).
 *
 * Drei Zustände:
 *   1. Kein Token in URL    → E-Mail-Eingabe + "Bestell-Link anfordern"
 *   2. Token in URL         → Lädt /buyer-portal/orders?token=... und zeigt
 *                             Tabellen für Shop-Bestellungen + Reservierungen
 *   3. Token abgelaufen     → Fehler-Anzeige + "Neuen Link anfordern"-Button
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
    Mail, Loader2, CheckCircle2, AlertCircle, Package, ShoppingBag,
    Bike, FileSignature, Download, Clock, ShieldCheck,
} from "lucide-react";
import { previewPdfResponse } from "../lib/pdfPreview";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtEur = (v) =>
    Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("de-DE", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

const STATUS_LABEL = {
    pending: "Offen",
    completed: "Bezahlt",
    initiated: "Gestartet",
    failed: "Fehlgeschlagen",
    expired: "Abgelaufen",
};

const STATUS_TONE = {
    pending: "bg-amber-100 text-amber-900 border-amber-300",
    completed: "bg-emerald-100 text-emerald-800 border-emerald-300",
    initiated: "bg-blue-100 text-blue-800 border-blue-300",
    failed: "bg-red-100 text-red-700 border-red-300",
    expired: "bg-gray-200 text-gray-700 border-gray-400",
};


function EmailRequestForm({ onSent }) {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (!email.trim()) return;
        setBusy(true);
        try {
            await axios.post(`${API}/api/buyer-portal/request-link`, {
                email: email.trim().toLowerCase(),
                origin_url: window.location.origin,
            });
            setDone(true);
            onSent?.(email);
        } catch (e2) {
            setErr(e2?.response?.data?.detail || "Anfrage fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    if (done) {
        return (
            <div className="text-center" data-testid="buyer-portal-mail-sent">
                <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-600" />
                <h2 className="font-display text-xl font-black mt-4">Prüfen Sie Ihren Posteingang</h2>
                <p className="text-sm text-gray-700 mt-2 max-w-sm mx-auto">
                    Falls Bestellungen oder Reservierungen unter <b>{email}</b> existieren,
                    haben wir Ihnen soeben einen Link zugeschickt (24 h gültig).
                </p>
                <button
                    type="button"
                    onClick={() => { setDone(false); setEmail(""); }}
                    className="mt-6 text-xs uppercase tracking-wider font-bold text-gray-500 hover:underline"
                    data-testid="buyer-portal-back-to-email"
                >
                    Andere E-Mail verwenden
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="space-y-3" data-testid="buyer-portal-email-form">
            <h2 className="font-display text-xl font-black">Meine Bestellungen</h2>
            <p className="text-sm text-gray-600">
                Sie haben über TwoWheels eine Bestellung oder Reservierung abgeschlossen?
                Geben Sie Ihre E-Mail-Adresse ein — wir schicken Ihnen einen sicheren
                Zugangs-Link.
            </p>
            <input
                type="email"
                required
                placeholder="ihre.email@beispiel.de"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-11 px-3 border-2 border-gray-300 rounded-sm focus:border-[#111827] outline-none"
                data-testid="buyer-portal-email-input"
            />
            {err && <div className="text-sm text-red-600">{err}</div>}
            <button
                type="submit"
                disabled={busy}
                className="w-full h-11 rounded-sm bg-[#111827] hover:bg-black text-white font-bold tracking-wider uppercase flex items-center justify-center gap-2"
                data-testid="buyer-portal-request-link-btn"
            >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Bestell-Link anfordern
            </button>
            <div className="flex items-center gap-1.5 text-[10px] text-gray-500 justify-center pt-2">
                <ShieldCheck className="w-3.5 h-3.5" />
                Login-frei · 24 h-Token · DSGVO-konform
            </div>
        </form>
    );
}


function TransactionRow({ t }) {
    return (
        <li className="border border-gray-200 rounded-sm p-3 bg-white" data-testid={`buyer-portal-tx-${t.session_id}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <ShoppingBag className="w-4 h-4 text-[#111827]" />
                        <span className="font-bold text-sm">Shop-Bestellung</span>
                        <span className="text-[10px] text-gray-500 font-mono-num">{t.session_id?.slice(0, 24)}…</span>
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                        bei <b>{t.dealer_name}</b> · {fmtDate(t.created_at)}
                    </div>
                    {t.lines && t.lines.length > 0 && (
                        <ul className="mt-2 space-y-0.5 text-[11px] text-gray-700">
                            {t.lines.map((l, i) => (
                                <li key={i} className="flex justify-between">
                                    <span>{l.quantity}× {l.name}</span>
                                    <span className="font-mono-num">{fmtEur(l.line_total_cents / 100)}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <div className="text-right shrink-0">
                    <div className="font-mono-num font-black text-base">{fmtEur(t.amount_eur)}</div>
                    <span className={`mt-1 inline-block px-2 py-0.5 border rounded-sm text-[10px] font-bold uppercase tracking-wider ${STATUS_TONE[t.status] || STATUS_TONE.pending}`}>
                        {STATUS_LABEL[t.status] || t.status}
                    </span>
                </div>
            </div>
        </li>
    );
}


function ReservationRow({ r, onPdf, onSign }) {
    const signed = !!r.contract_signed_at;
    return (
        <li className="border border-gray-200 rounded-sm p-3 bg-white" data-testid={`buyer-portal-res-${r.id}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Bike className="w-4 h-4 text-[#111827]" />
                        <span className="font-bold text-sm">{r.listing_title}</span>
                        {signed
                            ? <span className="px-2 py-0.5 border bg-emerald-100 text-emerald-800 border-emerald-300 rounded-sm text-[10px] font-bold uppercase">Signiert ✓</span>
                            : <span className="px-2 py-0.5 border bg-amber-100 text-amber-900 border-amber-300 rounded-sm text-[10px] font-bold uppercase">Reserviert</span>}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                        bei <b>{r.seller_name}</b>{r.seller_city ? ` · ${r.seller_city}` : ""}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-700 flex-wrap">
                        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDate(r.reserved_at)}</span>
                        {r.expires_at && !signed && (
                            <span className="inline-flex items-center gap-1 text-amber-700"><Clock className="w-3 h-3" /> bis {fmtDate(r.expires_at)}</span>
                        )}
                        {signed && (
                            <span className="inline-flex items-center gap-1 text-emerald-700"><FileSignature className="w-3 h-3" /> unterschrieben {fmtDate(r.contract_signed_at)}</span>
                        )}
                    </div>
                </div>
                <div className="text-right shrink-0">
                    {r.listing_price && <div className="font-mono-num font-black text-base">{fmtEur(r.listing_price)}</div>}
                </div>
            </div>
            <div className="mt-2 pt-2 border-t border-gray-100 flex gap-2 flex-wrap">
                <button
                    type="button"
                    onClick={() => onPdf(r)}
                    className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#2563EB] hover:underline"
                    data-testid={`buyer-portal-res-pdf-${r.id}`}
                >
                    <Download className="w-3 h-3" /> Vorvertrag PDF
                </button>
                {!signed && (
                    <button
                        type="button"
                        onClick={() => onSign(r)}
                        className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-emerald-700 hover:underline"
                        data-testid={`buyer-portal-res-sign-${r.id}`}
                    >
                        <FileSignature className="w-3 h-3" /> Online signieren
                    </button>
                )}
                {signed && (
                    <button
                        type="button"
                        onClick={() => onPdf(r, true)}
                        className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-emerald-700 hover:underline"
                        data-testid={`buyer-portal-res-signed-pdf-${r.id}`}
                    >
                        <Download className="w-3 h-3" /> Signierte PDF
                    </button>
                )}
            </div>
        </li>
    );
}


export default function MyOrdersPage() {
    const [params] = useSearchParams();
    const token = params.get("token");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const load = () => {
        if (!token) return;
        setLoading(true);
        axios.get(`${API}/api/buyer-portal/orders`, { params: { token } })
            .then((r) => { setData(r.data); setErr(""); })
            .catch((e) => setErr(e?.response?.data?.detail || "Link konnte nicht geöffnet werden"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const downloadPdf = async (r) => {
        // Phase 4g.Z — Token-gestützter Download direkt aus dem Magic-Link-Portal.
        try {
            const resp = await axios.get(`${API}/api/buyer-portal/reservation/${r.id}/contract.pdf`,
                { params: { token }, responseType: "blob" });
            previewPdfResponse(resp, `Kaufvertrag_${r.id.slice(0, 8)}.pdf`);
        } catch (e) {
            const status = e.response?.status;
            alert(
                status === 403 ? "Dieser Zugang gehört nicht zu dieser Reservierung."
                : status === 401 ? "Ihr Zugangslink ist abgelaufen — bitte einen neuen anfordern."
                : "Der Vertrag konnte nicht geladen werden. Bitte versuchen Sie es erneut."
            );
        }
    };

    const startSign = async (r) => {
        // Phase 4g.AA — Token-gestütztes Online-Signieren direkt aus dem Magic-Link.
        try {
            const resp = await axios.post(
                `${API}/api/buyer-portal/reservation/${r.id}/sign`,
                null,
                { params: { token } }
            );
            const d = resp.data || {};
            if (d.status === "already_signed") {
                alert("Dieser Vertrag wurde bereits signiert.");
            } else if (d.signing_url && !d.mocked) {
                window.open(d.signing_url, "_blank", "noopener,noreferrer");
            } else if (d.mocked) {
                alert("Signatur-Anfrage erstellt (Demo-/Testmodus). Sobald Skribble live geschaltet ist, öffnet sich hier die echte Signaturseite.");
                load();
            } else {
                alert("Signatur-Anfrage wurde gestartet. Sie erhalten in Kürze eine E-Mail zur Unterschrift.");
                load();
            }
        } catch (e) {
            const status = e.response?.status;
            alert(
                status === 403 ? "Dieser Zugang gehört nicht zu dieser Reservierung."
                : status === 401 ? "Ihr Zugangslink ist abgelaufen — bitte einen neuen anfordern."
                : status === 400 ? (e.response?.data?.detail || "Signatur konnte nicht gestartet werden.")
                : "Der Signatur-Vorgang konnte nicht gestartet werden. Bitte versuchen Sie es erneut."
            );
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="buyer-portal-no-token">
                <div className="max-w-md w-full bg-white border border-gray-200 rounded-sm p-8">
                    <EmailRequestForm />
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <Loader2 className="w-10 h-10 animate-spin text-[#FFCC00]" />
            </div>
        );
    }

    if (err) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-white border border-gray-200 rounded-sm p-8 text-center" data-testid="buyer-portal-error">
                    <AlertCircle className="w-12 h-12 mx-auto text-red-500" />
                    <h2 className="font-display text-xl font-black mt-4">Link ungültig</h2>
                    <p className="text-sm text-gray-700 mt-2">{err}</p>
                    <a href="/my-orders" className="inline-block mt-6 px-5 py-2.5 rounded-sm bg-[#FFCC00] hover:bg-[#FFD633] text-[#111827] font-bold uppercase tracking-wider text-xs">
                        Neuen Link anfordern
                    </a>
                </div>
            </div>
        );
    }

    const txns = data?.transactions || [];
    const reservations = data?.reservations || [];
    const isEmpty = txns.length === 0 && reservations.length === 0;

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-3xl mx-auto px-4">
                <header className="mb-6">
                    <h1 className="font-display text-3xl font-black tracking-tight">Meine Bestellungen</h1>
                    <p className="text-sm text-gray-600 mt-1">
                        <span className="font-bold">{data?.email}</span> ·
                        {" "}{txns.length} Shop-Bestellung{txns.length === 1 ? "" : "en"}
                        {" "}· {reservations.length} Reservierung{reservations.length === 1 ? "" : "en"}
                    </p>
                </header>

                {isEmpty && (
                    <div className="bg-white border border-gray-200 rounded-sm p-12 text-center" data-testid="buyer-portal-empty">
                        <Package className="w-12 h-12 mx-auto text-gray-300" />
                        <p className="mt-3 text-gray-600">Noch keine Bestellungen vorhanden.</p>
                    </div>
                )}

                {txns.length > 0 && (
                    <section className="mb-8">
                        <h2 className="font-display text-lg font-black mb-3 flex items-center gap-2">
                            <ShoppingBag className="w-5 h-5" /> Shop-Bestellungen
                        </h2>
                        <ul className="space-y-2" data-testid="buyer-portal-transactions">
                            {txns.map((t) => <TransactionRow key={t.session_id} t={t} />)}
                        </ul>
                    </section>
                )}

                {reservations.length > 0 && (
                    <section>
                        <h2 className="font-display text-lg font-black mb-3 flex items-center gap-2">
                            <Bike className="w-5 h-5" /> Reservierungen + Verträge
                        </h2>
                        <ul className="space-y-2" data-testid="buyer-portal-reservations">
                            {reservations.map((r) => (
                                <ReservationRow key={r.id} r={r} onPdf={downloadPdf} onSign={startSign} />
                            ))}
                        </ul>
                    </section>
                )}
            </div>
        </div>
    );
}
